import { useRef, useState, useContext, useLayoutEffect, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import uuid from "react-uuid";
import { isEmpty } from "lodash-es";

import {
    ChatMessage,
    Conversation,
    ConversationRequest,
    conversationStreamApi,
    ErrorMessage,
} from "../api";
import { AppStateContext } from "../state/AppProvider";

const enum MessageStatus {
    NotRunning = "Not Running",
    Processing = "Processing",
    Done = "Done",
}

const ASSISTANT = "assistant";
const TOOL = "tool";
const ERROR = "error";

/** Ensure a userId exists in localStorage. */
function getOrCreateUserId(): string {
    let userId = localStorage.getItem("userId");
    if (!userId) {
        userId = uuidv4();
        localStorage.setItem("userId", userId);
    }
    return userId;
}

/** Progress stage of the streamed turn, for the loading indicator. */
export type StatusStage = "" | "routing" | "searching" | "generating";

export interface UseChatReturn {
    messages: ChatMessage[];
    isLoading: boolean;
    showLoadingMessage: boolean;
    statusStage: StatusStage;
    processMessages: string;
    errorMsg: ErrorMessage | null | undefined;
    hideErrorDialog: boolean;
    makeApiRequest: (question: string, conversationId?: string) => Promise<void>;
    clearChat: () => Promise<void>;
    newChat: () => void;
    stopGenerating: () => void;
    toggleErrorDialog: () => void;
    handleErrorDialogClose: () => void;
}

export function useChat(): UseChatReturn {
    const appStateContext = useContext(AppStateContext);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showLoadingMessage, setShowLoadingMessage] = useState(false);
    const [statusStage, setStatusStage] = useState<StatusStage>("");
    const [processMessages, setProcessMessages] = useState<string>(MessageStatus.NotRunning);
    const [errorMsg, setErrorMsg] = useState<ErrorMessage | null | undefined>();
    const [hideErrorDialog, setHideErrorDialog] = useState(true);
    const abortFuncs = useRef<AbortController[]>([]);

    // Mutable accumulators for streaming — these don't trigger re-renders.
    const assistantContentRef = useRef("");
    const assistantMsgRef = useRef<ChatMessage>({} as ChatMessage);
    const toolMsgRef = useRef<ChatMessage>({} as ChatMessage);
    // Stable ids for the assistant/tool messages built during one streamed turn.
    const streamAssistantIdRef = useRef("");
    const streamToolIdRef = useRef("");

    // ── helpers ─────────────────────────────────────────────

    const toggleErrorDialog = useCallback(() => setHideErrorDialog((h) => !h), []);

    const handleErrorDialogClose = useCallback(() => {
        setHideErrorDialog(true);
        setTimeout(() => setErrorMsg(null), 500);
    }, []);

    // Accumulated tool-message content for the current turn. tool_results (cards)
    // arrive BEFORE generation, citations arrive after — both merge into the one
    // tool message that precedes the assistant message in the array.
    const toolContentRef = useRef<{ citations: any[]; tool_results: any[] }>({
        citations: [],
        tool_results: [],
    });

    // Rebuild the streaming tool message from the accumulated tool content.
    const rebuildToolMessage = useCallback(() => {
        const content = toolContentRef.current;
        if ((content.citations?.length ?? 0) === 0 && (content.tool_results?.length ?? 0) === 0) {
            return;
        }
        toolMsgRef.current = {
            id: streamToolIdRef.current,
            role: TOOL,
            content: JSON.stringify(content),
            date: new Date().toISOString(),
        };
    }, []);

    // Commit the in-progress tool + assistant messages to state. The tool
    // message is placed BEFORE the assistant so its cards/citations render with
    // the answer (Chat.tsx reads them from the preceding message).
    const commitStreaming = useCallback(() => {
        setMessages((prev) => {
            const base = prev.filter(
                (m) => m.id !== assistantMsgRef.current?.id && m.id !== toolMsgRef.current?.id
            );
            const streaming: ChatMessage[] = [];
            if (!isEmpty(toolMsgRef.current)) streaming.push(toolMsgRef.current);
            if (!isEmpty(assistantMsgRef.current)) streaming.push(assistantMsgRef.current);
            return [...base, ...streaming];
        });
    }, []);

    // ── SSE frame handler ───────────────────────────────────

    const handleSSEFrame = useCallback(
        (event: string, payload: any) => {
            switch (event) {
                case "status":
                    setStatusStage((payload.stage as StatusStage) || "");
                    setShowLoadingMessage(true);
                    break;
                case "tool_results": {
                    // Cards (e.g. booking slots) — render immediately, before the
                    // answer text streams.
                    toolContentRef.current.tool_results = payload.tool_results ?? [];
                    rebuildToolMessage();
                    commitStreaming();
                    break;
                }
                case "token": {
                    setShowLoadingMessage(false);
                    assistantContentRef.current += payload.delta ?? "";
                    assistantMsgRef.current = {
                        id: streamAssistantIdRef.current,
                        role: ASSISTANT,
                        content: assistantContentRef.current,
                        date: new Date().toISOString(),
                    };
                    commitStreaming();
                    break;
                }
                case "citations": {
                    toolContentRef.current.citations = payload.citations ?? [];
                    rebuildToolMessage();
                    commitStreaming();
                    break;
                }
                case "done": {
                    // Replace the accumulated stream text with the post-processed
                    // final answer (domain scrub / citation normalisation applied
                    // server-side after generation), and attach the Langfuse
                    // trace_id so OK/NG feedback can target this response's trace.
                    if (payload.answer) {
                        assistantContentRef.current = payload.answer;
                    }
                    assistantMsgRef.current = {
                        ...assistantMsgRef.current,
                        id: streamAssistantIdRef.current,
                        role: ASSISTANT,
                        content: assistantContentRef.current,
                        date: new Date().toISOString(),
                        trace_id: payload.trace_id ?? assistantMsgRef.current?.trace_id,
                    };
                    commitStreaming();
                    break;
                }
                case "error":
                    throw Error(payload.message || "stream error");
            }
        },
        [commitStreaming, rebuildToolMessage],
    );

    // ── SSE stream parser (event:/data: frames split by \n\n) ─

    const parseSSEStream = useCallback(
        async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            setProcessMessages(MessageStatus.Processing);

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let sepIndex: number;
                while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
                    const frame = buffer.slice(0, sepIndex);
                    buffer = buffer.slice(sepIndex + 2);

                    let event = "message";
                    let data = "";
                    for (const line of frame.split("\n")) {
                        if (line.startsWith("event:")) event = line.slice(6).trim();
                        else if (line.startsWith("data:")) data += line.slice(5).trim();
                    }
                    if (!data) continue;
                    let payload: any;
                    try {
                        payload = JSON.parse(data);
                    } catch {
                        continue;
                    }
                    handleSSEFrame(event, payload);
                }
            }
        },
        [handleSSEFrame],
    );

    // ── main API request ────────────────────────────────────

    const makeApiRequest = useCallback(
        async (question: string, conversationId?: string) => {
            setIsLoading(true);
            setShowLoadingMessage(true);
            setStatusStage("routing");
            assistantContentRef.current = "";
            assistantMsgRef.current = {} as ChatMessage;
            toolMsgRef.current = {} as ChatMessage;
            toolContentRef.current = { citations: [], tool_results: [] };
            streamAssistantIdRef.current = uuid();
            streamToolIdRef.current = uuid();

            const abortController = new AbortController();
            abortFuncs.current.unshift(abortController);
            const userId = getOrCreateUserId();

            const userMessage: ChatMessage = {
                id: uuid(),
                role: "user",
                content: question,
                date: new Date().toISOString(),
            };

            let conversation: Conversation;
            if (!conversationId) {
                conversation = {
                    id: conversationId ?? uuid(),
                    title: question,
                    messages: [userMessage],
                    date: new Date().toISOString(),
                };
            } else {
                const existing = appStateContext?.state?.currentChat;
                if (!existing) {
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter((a) => a !== abortController);
                    return;
                }
                conversation = existing;
                conversation.messages.push(userMessage);
            }

            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: conversation });
            setMessages(conversation.messages);

            const request: ConversationRequest = {
                messages: conversation.messages.filter((m) => m.role !== ERROR),
                userId,
            };

            try {
                const response = await conversationStreamApi(request, abortController.signal);
                if (!response.ok) {
                    let serverError = `Request failed (${response.status})`;
                    try {
                        const body = await response.json();
                        if (body?.error) serverError = body.error;
                    } catch {
                        /* non-JSON error body */
                    }
                    throw Error(serverError);
                }
                if (response?.body) {
                    await parseSSEStream(response.body.getReader());
                    if (!isEmpty(toolMsgRef.current)) {
                        conversation.messages.push(toolMsgRef.current);
                    }
                    if (!isEmpty(assistantMsgRef.current)) {
                        conversation.messages.push(assistantMsgRef.current);
                    }
                    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: conversation });
                    setMessages([...conversation.messages]);
                }
            } catch (e) {
                if (!abortController.signal.aborted) {
                    let errorMessage =
                        "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
                    if (e instanceof Error && e.message) errorMessage = e.message;

                    const errorChatMsg: ChatMessage = {
                        id: uuid(),
                        role: ERROR,
                        content: errorMessage,
                        date: new Date().toISOString(),
                    };
                    conversation.messages.push(errorChatMsg);
                    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: conversation });
                    setMessages((prev) => [...prev, errorChatMsg]);
                } else {
                    setMessages((prev) => [...prev, userMessage]);
                }
            } finally {
                setIsLoading(false);
                setShowLoadingMessage(false);
                setStatusStage("");
                abortFuncs.current = abortFuncs.current.filter((a) => a !== abortController);
                setProcessMessages(MessageStatus.Done);
            }
        },
        [appStateContext, parseSSEStream],
    );

    const clearChat = useCallback(async () => {
        setMessages([]);
        appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
    }, [appStateContext]);

    const newChat = useCallback(() => {
        setProcessMessages(MessageStatus.Processing);
        setMessages([]);
        appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
        setProcessMessages(MessageStatus.Done);
    }, [appStateContext]);

    const stopGenerating = useCallback(() => {
        abortFuncs.current.forEach((a) => a.abort());
        setShowLoadingMessage(false);
        setIsLoading(false);
    }, []);

    // ── sync messages from currentChat state ────────────────

    useEffect(() => {
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages);
        } else {
            setMessages([]);
        }
    }, [appStateContext?.state.currentChat]);

    // ── update state on processMessages === Done ────────────

    useLayoutEffect(() => {
        if (
            appStateContext &&
            appStateContext.state.currentChat &&
            processMessages === MessageStatus.Done
        ) {
            setMessages(appStateContext.state.currentChat.messages);
            setProcessMessages(MessageStatus.NotRunning);
        }
    }, [processMessages]);

    return {
        messages,
        isLoading,
        showLoadingMessage,
        statusStage,
        processMessages,
        errorMsg,
        hideErrorDialog,
        makeApiRequest,
        clearChat,
        newChat,
        stopGenerating,
        toggleErrorDialog,
        handleErrorDialogClose,
    };
}
